import { lazy } from "react";

// Number of times to (re)attempt the dynamic import before escalating to the
// reload recovery, and the backoff between attempts.
const IMPORT_ATTEMPTS = 3;
const RETRY_BACKOFF_MS = [250, 800];

function _delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Attempt the dynamic import a few times before giving up. On iOS WKWebView,
 * loading a local ES module chunk intermittently fails with "Importing a
 * module script failed" (transient — memory pressure, a background/foreground
 * hop, or a Capgo bundle swap in flight). A second attempt a moment later
 * almost always succeeds, so retrying here silently recovers the common case
 * WITHOUT a full reload or an ErrorBoundary crash. Only when every attempt
 * fails do we fall through to the stale-chunk reload recovery below.
 */
export async function _importWithRetry<T extends { default: React.ComponentType<any> }>(
  factory: () => Promise<T>
): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < IMPORT_ATTEMPTS; attempt++) {
    try {
      const mod = await factory();
      // Same missing-default guard as before: iOS WebView can *resolve* a
      // stale-chunk import to a module with no usable default (nginx served
      // index.html as the chunk). Treat that as a failed attempt.
      if (!mod || typeof mod.default === "undefined") {
        throw new Error("lazyRetry: resolved module has no default export (stale chunk?)");
      }
      return mod;
    } catch (err) {
      lastErr = err;
      if (attempt < IMPORT_ATTEMPTS - 1) {
        await _delay(RETRY_BACKOFF_MS[attempt] ?? 800);
      }
    }
  }
  throw lastErr;
}

/**
 * Drop-in replacement for React.lazy that recovers from the
 * stale-chunk-after-deploy crash.
 *
 * The failure mode: when a new build ships, the hashed chunk filenames
 * change. A device still running the old bundle that navigates to a
 * not-yet-loaded route requests an old `*.js` chunk that no longer
 * exists. nginx's SPA fallback then serves `index.html` (HTTP 200) for
 * that `.js` request, the browser tries to evaluate HTML as a module,
 * and the dynamic import rejects — surfacing to React.lazy as
 * `Cannot read properties of undefined (reading 'default')` and crashing
 * the whole route.
 *
 * lazyRetry catches that rejected import and does a one-time, debounced
 * full reload (`window.location.reload()`), which re-fetches the current
 * `index.html` + fresh chunk hashes and lands the user on the new bundle.
 * The reload is gated to once per 10s via sessionStorage so a genuinely
 * broken chunk (not just stale) can't pin the page in a reload loop — in
 * that case the error re-throws and the nearest ErrorBoundary handles it.
 *
 * `any`: generic React.ComponentType — any component props are acceptable.
 */
export function lazyRetry<T extends { default: React.ComponentType<any> }>(
  factory: () => Promise<T>
): React.LazyExoticComponent<T["default"]> {
  return lazy(() =>
    _importWithRetry(factory)
      .catch((err) => {
        const key = "chunk_reload_ts";
        const last = Number(sessionStorage.getItem(key) || 0);
        if (Date.now() - last > 10000) {
          sessionStorage.setItem(key, String(Date.now()));
          window.location.reload();
          // We ARE recovering — the page is reloading onto the fresh
          // bundle. Don't re-throw: a thrown error here would hit the
          // ErrorBoundary and get reported as a crash (Errors tab + Sentry
          // + alert email) even though we self-healed. Return a
          // never-settling promise so React keeps the Suspense fallback up
          // until the reload tears the page down — silent recovery.
          return new Promise<never>(() => { /* never resolves; page is reloading */ });
        }
        // We did NOT reload (debounced — this failure recurred within 10s of
        // the last one). That means a reload didn't fix it, so it's likely a
        // genuinely broken chunk, not just a stale one. Surface it to the
        // ErrorBoundary so it's reported as a real crash.
        throw err;
      })
  );
}
